'use client';

import { Trans } from '@lingui/macro';
import { Close, Report } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Tooltip, {
  type TooltipProps,
  tooltipClasses,
} from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { MarkdownRenderer } from '@prismo-io/design-system';
import { Application, ButtonLink } from '@prismo-io/router';
import type { UsersT } from '@prismo-io/schemas';
import { SupabaseImage } from '@prismo-io/utils/client/supabase-image';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState, useTransition } from 'react';
import { ConnectToToolBtn } from '../actions/connect/connect-to-tool-btn';
import type { ConnectResultSchemaT } from '../actions/connect/connect-to-tool.action';
import { ListenerStatusChange } from '../actions/listener-status-change/listener-status-change';
import { OAuth2Button } from '../actions/oauth2/oauth2.button';
import { UploadFileForm } from '../actions/upload-file/upload-file.form';
import type { GetIntegrationResultSchemaT } from '../api/get-integration.action';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    maxWidth: 220,
    boxShadow: theme.shadows[2],
    padding: 0,
  },
}));

type Props = GetIntegrationResultSchemaT & { user: Pick<UsersT, 'id' | 'url'> };

export const ActionBtn = (props: Props) => {
  const { isMe, user, tool, relation, hasCurrent } = props;

  const [isTransiting, setTransition] = useTransition();
  const [action, setAction] = useState<ConnectResultSchemaT | undefined>(
    undefined
  );

  const onResetAction = useCallback(() => {
    setAction(undefined);
  }, []);

  const router = useRouter();

  const onRefresh = useCallback(() => {
    setTransition(() => {
      setAction(undefined);
      return router.refresh();
    });
  }, [router]);

  const isFailed = useMemo(
    () => !hasCurrent && relation?.status === 'FAILED',
    [hasCurrent, relation?.status]
  );

  if (hasCurrent) {
    return (
      <ButtonLink
        variant="text"
        color="inherit"
        app={Application.APP}
        path="/profil/:user/:toolUrl"
        params={{
          user: user.url,
          toolUrl: tool.url,
        }}
      >
        <Trans>En savoir plus</Trans>
      </ButtonLink>
    );
  }

  if (!isMe) {
    return (
      <Typography>
        <Trans>Cet utilisateur n'a pas encore complété : {tool.name}</Trans>
      </Typography>
    );
  }

  if (relation?.status === 'IN_PROGRESS') {
    return (
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={20} />
        <Typography>
          <Trans>Outil en cours de traitement...</Trans>
        </Typography>
        <ListenerStatusChange relationId={relation.id} />
      </Stack>
    );
  }

  if (!action) {
    return (
      <HtmlTooltip
        title={
          isFailed ? (
            <Alert severity="error">
              <AlertTitle>
                <Trans>Une erreur est survenue.</Trans>
              </AlertTitle>
              <Trans>
                Veuillez réessayer la manipulation ou contactez-nous via notre
                chat.
              </Trans>
            </Alert>
          ) : undefined
        }
      >
        <Box>
          <ConnectToToolBtn
            startIcon={isFailed ? <Report /> : undefined}
            tool={tool}
            onClick={setAction}
            disabled={isTransiting}
          >
            <Trans>Démarrer</Trans>
          </ConnectToToolBtn>
        </Box>
      </HtmlTooltip>
    );
  }

  if (action) {
    switch (action.kind) {
      case 'UPLOAD_FILE':
        return (
          <>
            <ConnectToToolBtn
              startIcon={isFailed ? <Report /> : undefined}
              tool={tool}
              disabled
            >
              <Trans>Démarrer</Trans>
            </ConnectToToolBtn>
            <Dialog open onClose={onResetAction}>
              <DialogTitle>
                <Box component="div" position="relative" height="3rem">
                  <SupabaseImage
                    src={tool.logo}
                    alt={tool.name}
                    style={{ objectFit: 'contain', objectPosition: 'center' }}
                    fill
                    bucket="tsid"
                    replaceOnError={tool.name}
                  />
                </Box>
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={onResetAction}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <Close />
              </IconButton>
              <DialogContent>
                <Stack gap={2}>
                  {!!tool.description && (
                    <MarkdownRenderer>{tool.description}</MarkdownRenderer>
                  )}
                  <UploadFileForm
                    tool={tool}
                    action={action}
                    onComplete={onRefresh}
                  />
                </Stack>
              </DialogContent>
            </Dialog>
          </>
        );

      case 'OAUTH2':
        if (!!action.form && action.form?.length > 0) {
          return (
            <>
              <ConnectToToolBtn
                startIcon={isFailed ? <Report /> : undefined}
                tool={tool}
                disabled
              >
                <Trans>Démarrer</Trans>
              </ConnectToToolBtn>
              <Dialog open onClose={onRefresh}>
                <DialogTitle>
                  <Box component="div" position="relative" height="3rem">
                    <SupabaseImage
                      src={tool.logo}
                      alt={tool.name}
                      style={{ objectFit: 'contain', objectPosition: 'center' }}
                      fill
                      bucket="tsid"
                      replaceOnError={tool.name}
                    />
                  </Box>
                </DialogTitle>
                <IconButton
                  aria-label="close"
                  onClick={onRefresh}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}
                >
                  <Close />
                </IconButton>
                <DialogContent>
                  <Stack gap={2} minWidth="350px">
                    <OAuth2Button
                      tool={tool}
                      action={action}
                      onComplete={onRefresh}
                    />
                  </Stack>
                </DialogContent>
              </Dialog>
            </>
          );
        }

        return (
          <OAuth2Button tool={tool} action={action} onComplete={onRefresh} />
        );

      default:
        return null;
    }
  }

  return null;
};
